import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { UserProfile } from "@/components/users/UserProfile"
import { PostCard } from "@/components/posts/PostCard"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FileText, MessageCircle, Bookmark, Loader2 } from "lucide-react"
import { getUserByUsername, getPosts, isFollowing } from "@/lib/db"
import { useAuth } from "@/context/AuthContext"
import type { User, Post } from "@/types"

export default function UserProfilePage() {
  const { username } = useParams<{ username: string }>()
  const { firebaseUser } = useAuth()
  const [user, setUser] = useState<User | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [following, setFollowing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadUser() {
      if (!username) return

      try {
        setLoading(true)
        const userData = await getUserByUsername(username)

        if (!userData) {
          setError("User not found")
          return
        }

        setUser(userData)

        // Fetch the user's published posts
        const { posts: userPosts } = await getPosts({
          authorId: userData.uid,
          status: "published",
          limitCount: 20,
        })

        setPosts(userPosts)

        // Check if current user is following this user
        if (firebaseUser && userData.uid !== firebaseUser.id) {
          const followingStatus = await isFollowing(firebaseUser.id, userData.uid)
          setFollowing(followingStatus)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load user")
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [username, firebaseUser])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Not Found</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{error || "The user you're looking for doesn't exist."}</p>
        </div>
      </div>
    )
  }

  // Attach author info to each post for PostCard
  const postsWithAuthor = posts.map(post => ({
    ...post,
    author: {
      username: user.username,
      displayName: user.displayName,
      avatarUrl: user.avatarUrl,
    },
  }))

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-4xl">
        {/* Profile Header */}
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
          <UserProfile user={user} currentUserId={firebaseUser?.id} isFollowing={following} />
        </div>

        {/* Content Tabs */}
        <div className="mt-8">
          <Tabs defaultValue="posts">
            <TabsList>
              <TabsTrigger value="posts" className="flex items-center">
                <FileText className="mr-2 h-4 w-4" />
                Posts ({user.postCount})
              </TabsTrigger>
              <TabsTrigger value="comments" className="flex items-center">
                <MessageCircle className="mr-2 h-4 w-4" />
                Comments
              </TabsTrigger>
              <TabsTrigger value="saved" className="flex items-center">
                <Bookmark className="mr-2 h-4 w-4" />
                Saved
              </TabsTrigger>
            </TabsList>

            <TabsContent value="posts" className="mt-6 space-y-6">
              {postsWithAuthor.length > 0 ? (
                postsWithAuthor.map(post => <PostCard key={post.id} post={post} />)
              ) : (
                <div className="py-12 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No posts yet</h3>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">This user hasn&apos;t published any posts.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="comments" className="mt-6">
              <div className="py-12 text-center">
                <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No comments yet</h3>
              </div>
            </TabsContent>

            <TabsContent value="saved" className="mt-6">
              <div className="py-12 text-center">
                <Bookmark className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Saved posts are private</h3>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
